<template>
  <div class="nav">
    <Menu ref="menu" :model="items">
      <template #item="{ item, props }">
        <router-link v-if="item.route" v-slot="{ href, navigate }" :to="item.route" custom>
          <a :href="href" v-bind="props.action" @click="navigate">
            <i :class="['pi', 'pi-' + item.icon]"/>
            <span>{{ item.label }}</span>
          </a>
        </router-link>
      </template>
    </Menu>
  </div>
</template>

<script setup>
import { useAuth0 } from "@auth0/auth0-vue";
import { computed, onMounted, ref, watch } from "vue";
import { useRoute } from "vue-router";
import { useUsers } from "../composables/entities.js";

const route = useRoute();

const { isAuthenticated } = useAuth0();

const { canManageUsers, hasAdminAccess } = useUsers()


const items = computed(() => {
      const routes = [];
      if (isAuthenticated.value) {
        if (hasAdminAccess.value) {
          routes.push({ route: '/deployment-credentials', label: 'AI Deployment Credentials', icon: 'key' })
          routes.push({ route: '/ai-model-deployments', label: 'AI Model Deployments', icon: 'align-left' })
          routes.push({ route: '/knowledge-sources', label: 'Knowledge Sources', icon: 'book' })
          routes.push({ route: '/llm-quotas', label: 'LLM Quotas', icon: 'chart-bar' })
          routes.push({ route: '/search-credentials', label: 'Search Credentials', icon: 'search' })
          routes.push({ route: '/agent-skills', label: 'Agent Skills', icon: 'bolt' })
          routes.push({ route: '/agent-skill-groupings', label: 'Agent Skill Groupings', icon: 'th-large' })
          routes.push({ route: '/ai-agents', label: 'AI Agents', icon: 'android' })
          routes.push({ route: '/usage-metrics', label: 'Usage Metrics', icon: 'chart-line' })
        }
        if (canManageUsers.value) {
          routes.push({ route: '/users', label: 'Users', icon: 'user' })
          routes.push({ route: '/user-groups', label: 'User Groups', icon: 'users' })
        }
      }
      return routes
    }
)

const menu = ref(null)

watch(() => menu.value ? menu.value.focusedOptionId : null, (v) => {
  if (v === null || v === -1) {
    setMenuIndex()
  }
}, { immediate: true })

onMounted(() => {
  setMenuIndex()
})

function setMenuIndex() {
  const v = getMenuIndexFromPath()
  if (menu.value) {
    if (menu.value.focusedOptionIndex !== menu.value.id + '_' + v) {
      menu.value.focusedOptionIndex = menu.value.id + '_' + v
    }
  }
}

function getMenuIndexFromPath() {
  if (route.path.substring(0, 23) === '/deployment-credentials') {
    return '0'
  }
  if (route.path.substring(0, 20) === '/ai-model-deployments') {
    return '1'
  }
  if (route.path.substring(0, 18) === '/knowledge-sources') {
    return '2'
  }
  if (route.path.substring(0, 11) === '/llm-quotas') {
    return '3'
  }
  if (route.path.substring(0, 19) === '/search-credentials') {
    return '4'
  }
  if (route.path.substring(0, 13) === '/agent-skills') {
    return '5'
  }
  if (route.path.substring(0, 23) === '/agent-skill-groupings') {
    return '6'
  }
  if (route.path.substring(0, 10) === '/ai-agents') {
    return '7'
  }
  if (route.path.substring(0, 14) === '/usage-metrics') {
    return '8'
  }
  if (route.path.substring(0, 6) === '/users') {
    return '9'
  }
  if (route.path.substring(0, 12) === '/user-groups') {
    return '10'
  }
  return '2'
}
</script>

<style scoped>
@media (min-width: 1024px) {
  .nav {
    flex-basis: 20%;
    padding: 1.5rem;
  }
}

@media (max-width: 1023px) {
  .nav {
    padding: .5rem;
  }
}

a {
  color: #ffffff
}

a:hover {
  color: rgba(255, 255, 255, 0.75)
}

:deep(.p-menu) {
  background: initial;
  border: initial;
}

:deep(li[data-p-focused="true"]) {
  background-color: #60A5F9;
}

:deep(li:hover) {
  background-color: #60A5F9;
}

:deep(.p-menu .p-menuitem:not(.p-highlight):not(.p-disabled) > .p-menuitem-content:hover) {
  background-color: #60A5F9;
}

:deep(.p-menu .p-menuitem:not(.p-highlight):not(.p-disabled).p-focus > .p-menuitem-content) {
  background-color: #60A5F9;
}

@media (max-width: 1023px) {
  :deep(ul) {
    display: flex;
    flex-wrap: wrap;
    width: 100%;
  }
  :deep(li) {
    flex: 1;
  }
  :deep(li:last-child) {
    flex-grow: 2;
  }
}
</style>
